import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages the subscription vs one-time purchase selector on the PDP.
 *
 * Rather than owning its own product form, this component drives the nearest buy-buttons
 * product form by injecting / removing a `selling_plan` hidden input and toggling the
 * visibility / value of that form's quantity selector.
 *
 * Note: Extends HTMLElement directly (not Component) to avoid ref-ownership conflicts with
 * nested product-form-component elements. All DOM queries use querySelector scoped to `this`
 * or the containing section.
 */
class SubscriptionSelectorComponent extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #discountValue = 0;

  /** @type {number} */
  #variantPrice = 0;

  // ── Refs inside this component ────────────────────────────────

  get #oneTimeRadio() { return /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="oneTimeRadio"]')); }
  get #subscribeRadio() { return /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="subscribeRadio"]')); }
  get #oneTimeCard() { return this.querySelector('[ref="oneTimeCard"]'); }
  get #subscribeCard() { return this.querySelector('[ref="subscribeCard"]'); }
  get #subscribeDetails() { return /** @type {HTMLElement|null} */ (this.querySelector('[ref="subscribeDetails"]')); }
  get #subscribeBenefitsCollapsed() { return /** @type {HTMLElement|null} */ (this.querySelector('[ref="subscribeBenefitsCollapsed"]')); }
  get #bagsSelect() { return /** @type {HTMLSelectElement|null} */ (this.querySelector('[ref="bagsSelect"]')); }
  get #frequencySelect() { return /** @type {HTMLSelectElement|null} */ (this.querySelector('[ref="frequencySelect"]')); }
  get #subPriceEl() { return this.querySelector('[ref="subPrice"]'); }
  get #comparePriceEl() { return this.querySelector('[ref="comparePrice"]'); }
  get #oneTimePriceEl() { return this.querySelector('[ref="oneTimePrice"]'); }
  get #discountBadgeEl() { return this.querySelector('[ref="discountBadge"]'); }

  // ── Refs in the buy-buttons form (sibling block) ──────────────

  /** Container for scoping: same .product-details as this selector, or section/dialog/product-card fallback. */
  get #productDetailsScope() {
    return (
      this.closest('.product-details') ??
      this.closest('.shopify-section, dialog, product-card')
    );
  }

  /** The product form in the same product-details scope (from the buy-buttons block). */
  get #buyButtonsForm() {
    return this.#productDetailsScope?.querySelector(
      'product-form-component form[data-type="add-to-cart-form"]'
    ) ?? null;
  }

  /** The quantity input inside the buy-buttons form. */
  get #buyButtonsQtyInput() {
    return /** @type {HTMLInputElement|null} */ (
      this.#buyButtonsForm?.querySelector('quantity-selector-component input[name="quantity"]') ?? null
    );
  }

  /** The quantity-selector-component in the buy-buttons block (same product-details scope). */
  get #buyButtonsQtySelector() {
    return /** @type {HTMLElement|null} */ (
      this.#productDetailsScope?.querySelector(
        'product-form-component quantity-selector-component'
      ) ?? null
    );
  }

  // ── Lifecycle ─────────────────────────────────────────────────

  connectedCallback() {
    this.#discountValue = Number(this.dataset.discountValue) || 0;
    this.#variantPrice = Number(this.dataset.variantPrice) || 0;

    const { signal } = this.#abortController;

    this.addEventListener('change', this.#onFormChange, { signal });
    this.addEventListener('click', this.#onCardClick, { signal });

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Defer initial state so sibling blocks (buy-buttons) are in the DOM
    const applyInitial = () => {
      this.#applySelection(this.#subscribeRadio?.checked ? 'subscribe' : 'one-time');
    };
    setTimeout(applyInitial, 0);
    // Retry once after a short delay in case quantity selector renders late (e.g. content_for order)
    setTimeout(applyInitial, 150);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    // Clean up any injected inputs
    this.#removeInjectedSellingPlan();
  }

  // ── Private Handlers ──────────────────────────────────────────

  /** @param {Event} event */
  #onFormChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const refName = target.getAttribute('ref');

    // Radio toggle
    if (target instanceof HTMLInputElement && target.type === 'radio' && target.name.startsWith('purchase_type')) {
      this.#applySelection(/** @type {'one-time' | 'subscribe'} */ (target.value));
      return;
    }

    // Bags dropdown → update the buy-buttons quantity
    if (refName === 'bagsSelect' && target instanceof HTMLSelectElement) {
      this.#syncBuyButtonsQuantity(target.value);
      return;
    }

    // Frequency dropdown → update the injected selling_plan
    if (refName === 'frequencySelect' && target instanceof HTMLSelectElement) {
      const planInput = this.#getInjectedSellingPlanInput();
      if (planInput) planInput.value = target.value;
      return;
    }
  };

  /** @param {MouseEvent} event */
  #onCardClick = (event) => {
    const card = /** @type {HTMLElement} */ (event.target)?.closest('[data-option]');
    if (!card) return;

    const option = /** @type {HTMLElement} */ (card).dataset.option;
    const radio = option === 'subscribe' ? this.#subscribeRadio : this.#oneTimeRadio;

    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /** @param {Event} rawEvent */
  #onVariantUpdate = (rawEvent) => {
    const event = /** @type {import('@theme/events').VariantUpdateEvent} */ (rawEvent);
    const { resource, data } = event.detail;

    if (!resource) return;

    // Only update if this event is for the same section
    if (data.sectionId && data.sectionId !== this.dataset.sectionId) return;

    // Update prices
    this.#variantPrice = resource.price ?? this.#variantPrice;
    this.#updatePriceDisplay();
  };

  // ── Core Logic ────────────────────────────────────────────────

  /**
   * Applies the selection state — shows/hides panels and drives the buy-buttons form.
   * @param {'one-time' | 'subscribe'} type
   */
  #applySelection(type) {
    const isSubscribe = type === 'subscribe';

    // Drive the CSS rule via data-mode (hides buy-buttons quantity on initial load too)
    this.dataset.mode = type;

    // Active card styling
    this.#oneTimeCard?.classList.toggle('sub-option--active', !isSubscribe);
    this.#subscribeCard?.classList.toggle('sub-option--active', isSubscribe);

    // Panel visibility
    if (this.#subscribeDetails) this.#subscribeDetails.hidden = !isSubscribe;
    if (this.#subscribeBenefitsCollapsed) this.#subscribeBenefitsCollapsed.hidden = isSubscribe;

    // Buy-buttons quantity selector visibility (inline style as fallback)
    const qtySelector = this.#buyButtonsQtySelector;
    if (qtySelector) {
      qtySelector.style.display = isSubscribe ? 'none' : '';
      qtySelector.setAttribute('aria-hidden', isSubscribe ? 'true' : 'false');
    }

    if (isSubscribe) {
      // Inject / update selling_plan
      const planId = this.#frequencySelect?.value ?? this.dataset.firstPlanId ?? '';
      const planInput = this.#getOrCreateSellingPlanInput();
      if (planInput) planInput.value = planId;

      // Override quantity with bags value
      const bags = this.#bagsSelect?.value ?? String(Number(this.dataset.defaultBags) || 1);
      this.#syncBuyButtonsQuantity(bags);
    } else {
      // Remove selling_plan so normal one-time purchase is submitted
      this.#removeInjectedSellingPlan();

      // Reset quantity to 1 (buy-buttons quantity selector takes over)
      this.#syncBuyButtonsQuantity('1');
    }
  }

  /**
   * Returns the injected selling_plan input from the buy-buttons form, or creates and
   * appends it if it does not exist.
   * @returns {HTMLInputElement|null}
   */
  #getOrCreateSellingPlanInput() {
    const form = this.#buyButtonsForm;
    if (!form) return null;

    let input = /** @type {HTMLInputElement|null} */ (
      form.querySelector('input[name="selling_plan"][data-sub-selector]')
    );
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.dataset.subSelector = 'true';
      form.appendChild(input);
    }
    return input;
  }

  /**
   * Returns the existing injected selling_plan input without creating one.
   * @returns {HTMLInputElement|null}
   */
  #getInjectedSellingPlanInput() {
    return /** @type {HTMLInputElement|null} */ (
      this.#buyButtonsForm?.querySelector('input[name="selling_plan"][data-sub-selector]') ?? null
    );
  }

  /** Removes the injected selling_plan input from the buy-buttons form, if present. */
  #removeInjectedSellingPlan() {
    this.#getInjectedSellingPlanInput()?.remove();
  }

  /**
   * Sets the buy-buttons quantity input value and dispatches 'input' so the
   * quantity-selector-component stays in sync.
   * @param {string} value
   */
  #syncBuyButtonsQuantity(value) {
    const qtyInput = this.#buyButtonsQtyInput;
    if (!qtyInput) return;
    qtyInput.value = value;
    qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /** Updates the price display elements based on the current variant price and discount. */
  #updatePriceDisplay() {
    if (!this.#variantPrice) return;

    const format = (cents) => `$${(cents / 100).toFixed(2)}`;

    const subCents =
      this.#discountValue > 0
        ? Math.round((this.#variantPrice * (100 - this.#discountValue)) / 100)
        : this.#variantPrice;

    if (this.#oneTimePriceEl) this.#oneTimePriceEl.textContent = format(this.#variantPrice);
    if (this.#subPriceEl) this.#subPriceEl.textContent = format(subCents);
    if (this.#comparePriceEl) this.#comparePriceEl.textContent = format(this.#variantPrice);
    if (this.#discountBadgeEl) this.#discountBadgeEl.textContent = `${this.#discountValue}% off`;
  }
}

if (!customElements.get('subscription-selector-component')) {
  customElements.define('subscription-selector-component', SubscriptionSelectorComponent);
}
